import { shortName, type IsoDate, type MarkTypeDef, type Person, type RecentMark, type RecentMarksResponse } from '@kancelaria/shared';
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { api } from '../api/client.js';
import { CloseIcon } from './ui.js';

/** Скільки правок брати за раз: місяць активної роботи — кілька тисяч клітинок. */
const PAGE = 300;

/**
 * Правки, зроблені одним рухом: той самий автор, той самий шар і пауза між
 * сусідніми клітинками не більша за кілька секунд. Протяжка по тижню — це
 * одна дія на двадцять клітинок, а не двадцять рядків в історії.
 */
const SAME_ACTION_MS = 3000;

interface Action {
  key: string;
  at: Date;
  author: string;
  rows: RecentMark[];
}

/** SQLite пише час у UTC без позначки зони. */
const parseAt = (at: string): Date => new Date(`${at.replace(' ', 'T')}Z`);

function groupActions(rows: RecentMark[]): Action[] {
  const out: Action[] = [];
  for (const r of rows) {
    const at = parseAt(r.updatedAt);
    const last = out[out.length - 1];
    const prev = last?.rows[last.rows.length - 1];
    if (
      last &&
      prev &&
      prev.by === r.by &&
      prev.layer === r.layer &&
      Math.abs(parseAt(prev.updatedAt).getTime() - at.getTime()) <= SAME_ACTION_MS
    ) {
      last.rows.push(r);
      continue;
    }
    out.push({
      key: String(r.id),
      at,
      // Без автора пишуть бот і сторінка курсанта (підказки) та імпорт.
      author: r.byName ?? (r.layer === 'hint' ? 'Курсант через бота' : 'Без автора'),
      rows: [r],
    });
  }
  return out;
}

const dayKey = (d: Date): string => d.toDateString();

function dayLabel(d: Date): string {
  const today = new Date();
  const yesterday = new Date(today.getTime() - 864e5);
  if (dayKey(d) === dayKey(today)) return 'Сьогодні';
  if (dayKey(d) === dayKey(yesterday)) return 'Вчора';
  return d.toLocaleDateString('uk-UA', { day: 'numeric', month: 'long' });
}

const timeLabel = (d: Date): string => d.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });

/** Літера позначки в її кольорі — як у клітинці таблиці; порожньо — риска. */
function Mark({ code, types }: { code: string | null; types: MarkTypeDef[] }): ReactNode {
  if (!code) {
    return (
      <span className="history-mark empty" title="Порожньо">
        —
      </span>
    );
  }
  const t = types.find(x => x.code === code);
  const style = { '--hm-bg': `var(--mk-${code}-bg)`, '--hm-fg': `var(--mk-${code}-fg)` } as CSSProperties;
  return (
    <span className="history-mark" style={style} title={t?.label}>
      {t?.letter ?? code}
    </span>
  );
}

/**
 * Що зробила дія — по рядку на кожен перехід «було → стало»: дві літери зі
 * стрілкою, а поруч словами, що саме і з якою позначкою. Одна протяжка буває
 * змішаною (десь стояло «н», десь порожньо), тож переходів може бути кілька.
 */
function Changes({ rows, types }: { rows: RecentMark[]; types: MarkTypeDef[] }): ReactNode {
  // Назва без пояснень у дужках: «Присутній на прийомі їжі (наприклад …)».
  const name = (code: string): string =>
    (types.find(t => t.code === code)?.label ?? code).replace(/\s*\(.*$/, '');
  const moves = new Map<string, { from: string | null; to: string | null; n: number }>();
  const seen = new Set<string>();
  for (const r of rows) {
    // Ту саму клітинку могли правити двічі за дію — рахуємо останню правку.
    const cell = `${r.personId}|${r.date}|${r.meal}`;
    if (seen.has(cell)) continue;
    seen.add(cell);
    const key = `${r.prevKind ?? ''}>${r.kind ?? ''}`;
    const m = moves.get(key);
    if (m) m.n += 1;
    else moves.set(key, { from: r.prevKind, to: r.kind, n: 1 });
  }
  const hint = rows[0]?.layer === 'hint';
  return (
    <>
      {[...moves.values()].map(m => (
        <div className="history-change" key={`${m.from}>${m.to}`}>
          <Mark code={m.from} types={types} />
          <span className="history-arrow" aria-hidden="true">
            →
          </span>
          <Mark code={m.to} types={types} />
          <span className="history-words">
            {m.to && !m.from
              ? `Поставлено: ${name(m.to)}`
              : !m.to && m.from
                ? `Знято: ${name(m.from)}`
                : m.to && m.from
                  ? `Замінено: ${name(m.from)} → ${name(m.to)}`
                  : 'Без змін'}
            {hint ? ', підказка' : ''}
            {m.n > 1 ? <span className="history-count"> × {m.n}</span> : null}
          </span>
        </div>
      ))}
    </>
  );
}

export interface HistoryHighlight {
  keys: Set<string>;
  gone: Map<string, string>;
}

/** Ключі клітинок для підсвітки в таблиці — ті самі, що читає `Grid`. */
function highlightOf(rows: RecentMark[]): HistoryHighlight {
  const keys = new Set<string>();
  const gone = new Map<string, string>();
  const seen = new Set<string>();
  for (const m of rows) {
    const cell = `${m.personId}|${m.date}|${m.meal}`;
    // Новіші правки йдуть першими: клітинку описує остання з них.
    if (seen.has(cell)) continue;
    seen.add(cell);
    keys.add(cell);
    keys.add(`${m.personId}|${m.date}`);
    if (m.kind === null && m.prevKind) {
      gone.set(cell, m.prevKind);
      const day = `${m.personId}|${m.date}`;
      if (!gone.has(day)) gone.set(day, m.prevKind);
    }
  }
  return { keys, gone };
}

/**
 * Історія змін таблиці: хто, коли й що поставив за відкритий місяць.
 *
 * Панель збоку, а не вікно: таблицю під нею видно, і обрана дія світиться в
 * ній. Гортається вглиб пачками, скільки завгодно, — а не «останні 50».
 */
export function HistoryPanel({
  layers,
  from,
  to,
  people,
  types,
  reloadKey,
  onHighlight,
  onClose,
}: {
  layers: string[];
  from: IsoDate;
  to: IsoDate;
  people: Person[];
  types: MarkTypeDef[];
  reloadKey: unknown;
  onHighlight: (h: HistoryHighlight) => void;
  onClose: () => void;
}): ReactNode {
  const [rows, setRows] = useState<RecentMark[]>([]);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(true);
  const [picked, setPicked] = useState<string | 'all' | null>(null);
  const layersKey = layers.join(',');

  const load = async (before?: number): Promise<RecentMarksResponse> =>
    api.get<RecentMarksResponse>(
      `/api/marks/recent?from=${from}&to=${to}&layers=${layersKey}&limit=${PAGE}${before ? `&before=${before}` : ''}`,
    );

  // Новий місяць, таблиця чи свіжі правки — історія з початку.
  useEffect(() => {
    let cancel = false;
    setBusy(true);
    load()
      .then(r => {
        if (cancel) return;
        setRows(r.marks);
        setMore(r.more);
      })
      .catch(() => {
        if (!cancel) setRows([]);
      })
      .finally(() => {
        if (!cancel) setBusy(false);
      });
    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, layersKey, reloadKey]);

  const loadMore = async (): Promise<void> => {
    const last = rows[rows.length - 1];
    if (!last) return;
    setBusy(true);
    try {
      const r = await load(last.id);
      setRows(prev => [...prev, ...r.marks]);
      setMore(r.more);
    } finally {
      setBusy(false);
    }
  };

  const actions = useMemo(() => groupActions(rows), [rows]);
  const byId = useMemo(() => new Map(people.map(p => [p.id, p])), [people]);
  // Нічого не обрано — світиться найсвіжіша дія: відкрили історію, щоб
  // побачити, чим щойно скінчили.
  const current = picked ?? actions[0]?.key ?? null;

  useEffect(() => {
    if (current === 'all') onHighlight(highlightOf(rows));
    else onHighlight(highlightOf(actions.find(a => a.key === current)?.rows ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, actions]);

  useEffect(() => {
    const esc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  let lastDay = '';
  return (
    <aside className="history-panel" aria-label="Історія змін">
      <header className="history-head">
        <b>Історія змін</b>
        <button
          type="button"
          className={`btn sm${current === 'all' ? ' primary' : ''}`}
          disabled={!rows.length}
          title="Підсвітити в таблиці всі завантажені зміни"
          onClick={() => setPicked(current === 'all' ? null : 'all')}
        >
          Усі
        </button>
        <button type="button" className="btn ghost sm" aria-label="Закрити" onClick={onClose}>
          <CloseIcon />
        </button>
      </header>
      <div className="history-list">
        {!actions.length ? (
          <p className="note">{busy ? 'Завантажую…' : 'За цей місяць змін немає.'}</p>
        ) : null}
        {actions.map(a => {
          const day = dayKey(a.at);
          const head = day !== lastDay ? <div className="history-day">{dayLabel(a.at)}</div> : null;
          lastDay = day;
          const who = [...new Set(a.rows.map(r => r.personId))];
          const names = who.slice(0, 3).map(id => {
            const p = byId.get(id);
            return p ? shortName(p) : 'вибулий';
          });
          return (
            <div key={a.key}>
              {head}
              <button
                type="button"
                className={`history-item${current === a.key ? ' on' : ''}`}
                onClick={() => setPicked(a.key)}
              >
                <div className="history-line">
                  <span className="history-time">{timeLabel(a.at)}</span>
                  <span className="history-author">{a.author}</span>
                </div>
                <div className="history-what">
                  <Changes rows={a.rows} types={types} />
                </div>
                <div className="history-whom">
                  {names.join(', ')}
                  {who.length > 3 ? ` і ще ${who.length - 3}` : ''}
                </div>
              </button>
            </div>
          );
        })}
        {more ? (
          <button type="button" className="btn history-more" disabled={busy} onClick={() => void loadMore()}>
            {busy ? 'Завантажую…' : 'Показати давніші'}
          </button>
        ) : null}
      </div>
    </aside>
  );
}
